import type { UseQueryOptions } from '@tanstack/react-query';
import { useIsFetching, useQuery } from '@tanstack/react-query';
import { useChatApiClient } from '@trinity/chat-components/containers/Chat/useApiClient.js';
import type { ContentSummary, ConferenceTagSummaryItem } from '@/types';

type Props =
  & ITranscriptId;

type Data = {
  content: ContentSummary;
  items: ConferenceTagSummaryItem[];
};
type Options = Omit<UseQueryOptions<Data, unknown, Data, QueryKey>, 'queryKey' | 'queryFn'>;
type QueryKey = readonly [string, number];

export function getTranscriptSummaryQueryKey(data: ITranscriptId) {
  return [
    `get:transcripts/summary`,
    data.transcriptId,
  ] as const;
}

export const useTranscriptSummaryLoading = (data: ITranscriptId) => {
  const key = getTranscriptSummaryQueryKey(data);

  return useIsFetching({ queryKey: key }) > 0;
};

export const useFetchTranscriptSummary = (data: Props, options?: Options) => {
  const queryKey = getTranscriptSummaryQueryKey(data);

  const apiClient = useChatApiClient();

  return useQuery({
    queryKey,
    queryFn: ({ queryKey }) => {
      const [, transcriptId] = queryKey;

      return apiClient.transcriptsSummary.fetchSummary({
        transcriptId,
      });
    },
    ...options,
    refetchOnWindowFocus: false,
  });
};